import Typography from "@mui/material/Typography";
import { FilterChip } from "../FilterChip";
import { LocationsPayload } from "./types";
import { LOCATION_TYPES } from "./constants";
import React, { useCallback, useMemo } from "react";
import { LocationsFilter } from "./LocationsFilter";
import { formatCountLabel } from "../../../utils/text";

interface LocationsFilterChipProps {
  selectedValues: LocationsPayload;
  onValuesChange: (payload: LocationsPayload) => void;
  showAllMode?: boolean;
  highlighted?: boolean;
  id?: string;
}

export const LocationsFilterChip: React.FC<LocationsFilterChipProps> = ({
  selectedValues,
  onValuesChange,
  showAllMode = false,
  highlighted,
  id = "locations",
}: LocationsFilterChipProps) => {
  const getSelectedCount = useCallback(() => {
    switch (selectedValues.selectedLocationType) {
      case LOCATION_TYPES.COUNTRY:
        return selectedValues.countryAlpha2s.length;
      case LOCATION_TYPES.REGION:
        return selectedValues.regionIds.length;
      case LOCATION_TYPES.STATE:
        return selectedValues.stateIds.length;
      default:
        return 0;
    }
  }, [selectedValues]);

  const label = useMemo(() => {
    const hasNoSelections =
      selectedValues.countryAlpha2s.length === 0 &&
      selectedValues.regionIds.length === 0 &&
      selectedValues.stateIds.length === 0;

    if (hasNoSelections) {
      return "Geography";
    }

    switch (selectedValues.selectedLocationType) {
      case LOCATION_TYPES.COUNTRY:
        return "Country";
      case LOCATION_TYPES.REGION:
        return "Region";
      case LOCATION_TYPES.STATE:
        return "State";
      default:
        return "Geography";
    }
  }, [selectedValues]);

  const showAllModeCountLabel = useMemo(() => {
    if (label === "Geography") return "All";
    return formatCountLabel(label, getSelectedCount(), true);
  }, [label, getSelectedCount]);

  // Pass the exact same values we received - no local state!
  const handleFilterChange = useCallback(
    (payload: LocationsPayload) => {
      onValuesChange(payload);
    },
    [onValuesChange]
  );

  return (
    <FilterChip
      id={id}
      label={label}
      getSelectedCount={getSelectedCount}
      highlighted={highlighted}
      popoverOptions={{ title: "Locations", disableActionButtons: true }}
      renderLabel={(label, count) => {
        if (showAllMode) {
          return (
            <Typography component="span" color="text.primary">
              Geographies: {showAllModeCountLabel}
            </Typography>
          );
        }
        return (
          <Typography component="span" color="textSecondary">
            {formatCountLabel(label, count)}
          </Typography>
        );
      }}
    >
      <LocationsFilter
        selectedValues={selectedValues}
        onValuesChange={handleFilterChange}
      />
    </FilterChip>
  );
};
